// FEATURES C2 — one wording for a batch's real state in the admin (Batch
// Management list, Diploma report). The state itself is computed by the API
// (GET /v1/batch-sessions/batches → state, closesAt, overCapacity) with the
// same state machine the public page and checkout use; this file only names it.
import type { BatchState } from 'src/@types/landing/landing.contract';
import { formatDay } from '../landing/rules/format';

export type BatchStateColor = 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error';

/** A row of GET /v1/batch-sessions/batches. */
export type BatchListRow = {
  diploma: string;
  diplomaTitle?: string;
  batchId?: string;
  batchName: string;
  number?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  enrolUntil?: string | null;
  capacity?: number | null;
  studentCount?: number;
  active?: boolean;
  state?: BatchState;
  bookable?: boolean;
  closesAt?: string | null;
  overCapacity?: boolean;
};

const STATE: Record<BatchState, { label: string; color: BatchStateColor }> = {
  open: { label: 'Open', color: 'success' },
  starting_soon: { label: 'Starting soon', color: 'info' },
  late_join: { label: 'Late join', color: 'warning' },
  full: { label: 'Full', color: 'error' },
  closed: { label: 'Closed', color: 'default' },
  finished: { label: 'Finished', color: 'default' },
  hidden: { label: 'Hidden', color: 'default' },
};

/**
 * "Open", "Starting soon", "Late join until 6 Oct 2026", "Full", "Closed",
 * "Finished", "Hidden (inactive)" / "Hidden (no start date)".
 */
export function batchStateChip(row: Pick<BatchListRow, 'state' | 'closesAt' | 'active' | 'startDate'>): {
  label: string;
  color: BatchStateColor;
  hint: string;
} {
  const state: BatchState = row.state || 'hidden';
  const s = STATE[state] || STATE.hidden;
  if (state === 'late_join' && row.closesAt) {
    return { label: `Late join until ${formatDay(row.closesAt)}`, color: s.color, hint: 'Started; students can still join until that day (23:59 Cairo).' };
  }
  if (state === 'hidden') {
    return row.active === false
      ? { label: 'Hidden (inactive)', color: s.color, hint: 'Not on the page and not for sale: the batch is switched off.' }
      : { label: 'Hidden (no start date)', color: s.color, hint: 'Not on the page: set a start date to sell it.' };
  }
  const hints: Record<BatchState, string> = {
    open: 'On the page and for sale.',
    starting_soon: 'On the page and for sale; starts within 7 days.',
    late_join: '',
    full: 'On the page but every seat is taken.',
    closed: 'The last day to join has passed.',
    finished: 'The batch has ended.',
    hidden: '',
  };
  return { label: s.label, color: s.color, hint: hints[state] };
}

/** The last day to join as the admin set it, else the start day (what the page uses). */
export function lastDayToJoin(row: Pick<BatchListRow, 'enrolUntil' | 'startDate'>): { day: string; isStartDay: boolean } {
  if (row.enrolUntil) return { day: formatDay(row.enrolUntil), isStartDay: false };
  if (row.startDate) return { day: formatDay(row.startDate), isStartDay: true };
  return { day: '', isStartDay: false };
}
